import { useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import _ from 'lodash'

import { Modal } from '@material-ui/core'

import { connect } from 'react-redux'
import { requestFile, downloadPdfFromBase64Response, downloadPdfFromPayload } from 'utils/request'
import { getUser } from 'common/selectors/user'
import ReportModalForm from '../components/AddReportModalForm'
import styles from './AddReportModalContainer.module.scss'

const getBrowserTimeZone = () => {
  if (typeof window === 'undefined' || !window.Intl) {
    return null
  }

  return new window.Intl.DateTimeFormat().resolvedOptions().timeZone
}

const LONG_REPORT_THRESHOLD = 90

const AddReportModalContainer = props => {
  const [open] = useState(true)
  const { hideModal, systems, userId, user, userEmail } = props

  const systemsByGroup = _.groupBy(systems, 'group')
  const [error, setError] = useState('')
  const [emailPending, setEmailPending] = useState(null)
  const getDayWithSuffix = day => {
    if (day > 3 && day < 21) return `${day}th` // Handle 11th, 12th, 13th
    switch (day % 10) {
      case 1:
        return `${day}st`
      case 2:
        return `${day}nd`
      case 3:
        return `${day}rd`
      default:
        return `${day}th`
    }
  }

  const formatDateWithSuffix = (date, locale, options) => {
    const formattedDate = date.toLocaleDateString(locale, options)
    const [month, day, year] = formattedDate.split(' ')
    const dayWithSuffix = getDayWithSuffix(parseInt(day))
    return `${month} ${dayWithSuffix}, ${year}`
  }
  const handleSubmit = async data => {
    if (props.isSubmitting) return

    const reportTimeZone = getBrowserTimeZone()
    if (!reportTimeZone) {
      setError('statusCode:timezone')
      return
    }

    const startDate = new Date(data.startDate.toString())

    const endDate = new Date(data.endDate.toString())
    const now = new Date()
    const submitData = {
      systemId: data.deviceId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      startDateFormatted:
        user.toJS().language === 'es'
          ? data.startDate
              .toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
              .replace(/\sde\s/g, ' ')
          : formatDateWithSuffix(data.startDate, 'en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
      endDateFormatted:
        user.toJS().language === 'es'
          ? data.endDate
              .toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
              .replace(/\sde\s/g, ' ')
          : formatDateWithSuffix(data.endDate, 'en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
      startHour: data.firstOcupationStart,
      endHour: data.firstOcupationEnd,
      todaysDate:
        user.toJS().language === 'es'
          ? now
              .toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
              .replace(/\sde\s/g, ' ')
          : formatDateWithSuffix(now, 'en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }),
      name: data.name,
      location: data.location,
      occupationDays: data.occupationDays,
      params: data.params,
      language: user.toJS().language,
      customerId: userId,
      micasType: data.micasType,
      micasName: data.micasName,
      reportTimeZone,
      period1: data.period1
    }
    if (data.period2) submitData.period2 = data.period2

    if (data.secondOcupationStart) submitData.secondOcupationStart = data.secondOcupationStart
    if (data.secondOcupationEnd) submitData.secondOcupationEnd = data.secondOcupationEnd
    if (data.notificationEmail) submitData.notificationEmail = data.notificationEmail

    try {
      const response = await requestFile(`reports/`, {
        method: 'POST',
        data: {
          ...submitData
        }
      })

      if (!response || response === 413) {
        setError('statusCode:405')
        return
      }

      const contentType = response.headers.get('content-type') || ''
      let payload = null
      if (contentType.includes('application/json')) {
        payload = await response.json()
        if (payload?.status === 'email-pending') {
          setEmailPending({
            email: payload.email,
            threshold: payload.threshold
          })
          return
        }
        if (payload?.reult === 'worked' && payload?.pdf) {
          await downloadPdfFromPayload(payload, submitData.name)
          hideModal()
          return
        }
      }

      const worked = response.headers.get('reult')

      if (worked === 'worked') {
        await downloadPdfFromBase64Response(response, submitData.name, payload)
        hideModal()
      } else if (worked === 'there is no data') {
        setError('statusCode:409')
      } else {
        setError('statusCode:405')
      }
    } catch {
      setError('statusCode:405')
    }
  }

  const handleGenerateAnother = () => {
    setEmailPending(null)
    setError('')
  }

  return (
    <Modal
      open={open}
      onClose={hideModal}
      arial-labelledby='simple-modal-title'
      arial-desccribedby='simple-modal-description'
    >
      <div className={styles.content}>
        <ReportModalForm
          {...props}
          onSubmit={handleSubmit}
          systems={systems}
          systemsByGroup={systemsByGroup}
          error={error}
          emailPending={emailPending}
          userEmail={userEmail}
          longReportThreshold={LONG_REPORT_THRESHOLD}
          onGenerateAnother={handleGenerateAnother}
        />
      </div>
    </Modal>
  )
}

AddReportModalContainer.propTypes = {
  hideModal: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  title: PropTypes.string,
  contentText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  buttonName: PropTypes.string
}

const mapStateToProps = state => {
  const user = getUser(state)
  return {
    userEmail: user ? user.get('email') : ''
  }
}

export default withLocalize(connect(mapStateToProps)(AddReportModalContainer))
